import {createPromotion, updatePromotion} from '@/services/PromotionsService/promotions'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {PromotionStatus} from '@/types/promotion'
import {ROUTES} from '@/utils/constants/routesConstants'
import {cleanEmptyFields} from '@/utils/objectUtils'
import {transformToLayeredPromotion} from '@/utils/promotions/promotions'
import {decodePromotionUrl, encodePromotionUrl} from '@/utils/urlUtils'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import {useCallback} from 'react'
import {useDispatch} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import {useFeatureFlagLanguages} from '@/hooks/useFeatureFlagLanguages'

const MESSAGES = {
  draft: {success: 'Promotion saved as draft successfully.', error: 'Failed to save promotion as draft.'},
  create: {success: 'Promotion created successfully.', error: 'Failed to create promotion.'},
  update: {success: 'Promotion updated successfully.', error: 'Failed to update promotion.'},
}

const getMessageKey = ({isDraft, isCreating}) => (isDraft ? 'draft' : isCreating ? 'create' : 'update')

export const usePromotionOperations = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const languages = useFeatureFlagLanguages()

  const showToast = useCallback(
    (message, type = 'success') => {
      dispatch(setToastNotifications({show: true, message, type}))
    },
    [dispatch]
  )

  const decodeBannerUrl = data => {
    if (data?.data?.bannerUrl) {
      data.data.bannerUrl = decodePromotionUrl(data.data.bannerUrl)
    }
  }

  const preparePromotion = promotion => {
    const cleaned = cleanEmptyFields(promotion)
    return transformToLayeredPromotion(cleaned)
  }

  // Keep only text entries whose language exists in feature-flag languages.
  const removeIncorrectLanguageField = promotion => {
    if (!promotion?.text) {
      return promotion
    }

    const acceptedLanguageCodes = languages.map(lang => lang.code)

    return {
      ...promotion,
      text: Object.fromEntries(
        Object.entries(promotion.text).filter(([languageCode]) => {
          return acceptedLanguageCodes.includes(languageCode)
        })
      ),
    }
  }

  // Helper function to transform promotion data for API
  const transformPromotionForAPI = promotion => {
    let transformedPromotion = removeIncorrectLanguageField(promotion)
    transformedPromotion = JSON.parse(JSON.stringify(transformedPromotion))

    if (transformedPromotion?.bannerUrl) {
      transformedPromotion.bannerUrl = encodePromotionUrl(transformedPromotion.bannerUrl)
    }

    delete transformedPromotion.activeStartTime
    delete transformedPromotion.activeEndTime

    return transformedPromotion
  }

  const createWithStatus = async (promotion, status) => {
    const transformedPromotion = transformPromotionForAPI({...promotion, status})
    return await createPromotion(transformedPromotion)
  }

  // Mutation for creating/saving promotion as draft
  const {
    mutateAsync: draftMutateAsync,
    isPending: isDraftSaving,
    error: draftError,
  } = useMutation({
    mutationFn: async ({promotion}) => createWithStatus(promotion, PromotionStatus.DRAFT),
    onSuccess: (data, variables) => {
      decodeBannerUrl(data)

      if (variables.options?.isSavingForImageUpload) return

      showToast('Promotion saved as draft successfully')
      queryClient.invalidateQueries({queryKey: ['PROMOTION_DETAIL']})
      navigate(ROUTES.PROMOTIONS_LIST)
    },
    onError: error => {
      console.error('Failed to save promotion as draft:', error)
      showToast('Failed to save promotion as draft', 'error')
    },
  })

  // Mutation for saving/publishing promotion
  const {
    mutateAsync: saveMutateAsync,
    isPending: isSaving,
    error: saveError,
  } = useMutation({
    mutationFn: async promotion => createWithStatus(promotion, PromotionStatus.PUBLISHED),
    onSuccess: data => {
      decodeBannerUrl(data)
      showToast('Promotion created successfully.')
      queryClient.invalidateQueries({queryKey: ['PROMOTION_DETAIL']})
      navigate(ROUTES.PROMOTIONS_LIST)
      setTimeout(() => {
        window.location.reload()
      }, 100)
    },
    onError: error => {
      console.error('Failed to create promotion:', error)
      showToast('Failed to create promotion.', 'error')
    },
  })

  const {
    mutateAsync: updateMutateAsync,
    isPending: isUpdating,
    error: updateError,
  } = useMutation({
    mutationFn: async ({promotion, isDraft = false}) => {
      const transformedPromotion = transformPromotionForAPI({
        ...promotion,
        status: isDraft ? PromotionStatus.DRAFT : PromotionStatus.PUBLISHED,
      })
      return await updatePromotion(promotion?.id, transformedPromotion)
    },
    onSuccess: (data, variables) => {
      decodeBannerUrl(data)
      const key = getMessageKey(variables)
      showToast(MESSAGES[key].success)
      queryClient.invalidateQueries({queryKey: ['PROMOTION_DETAIL']})
      navigate(ROUTES.PROMOTIONS_LIST)
    },
    onError: (error, variables) => {
      const key = getMessageKey(variables)
      console.error(MESSAGES[key].error, error)
      showToast(MESSAGES[key].error, 'error')
    },
  })

  const handleDraft = useCallback(
    async (promotion, options = {isSavingForImageUpload: false}) => {
      if (!promotion?.name || promotion?.name.trim() === '') {
        showToast('Promotion Name is required to save as Draft', 'error')
        return
      }

      try {
        delete promotion?.promotionType
        return await draftMutateAsync({promotion: preparePromotion(promotion), options})
      } catch (error) {
        showToast(error?.data?.message || 'Failed to save promotion as draft', 'error')
        throw error
      }
    },
    [draftMutateAsync, showToast]
  )

  const savePromotion = useCallback(
    async promotion => {
      return await saveMutateAsync(preparePromotion(promotion))
    },
    [saveMutateAsync]
  )

  const handleUpdatePromotion = useCallback(
    async (promotionData, {isDraft = false, isCreating = false} = {}) => {
      return await updateMutateAsync({
        promotion: preparePromotion(promotionData),
        isDraft,
        isCreating,
      })
    },
    [updateMutateAsync]
  )

  return {
    handleDraft,
    savePromotion,
    handleUpdatePromotion,
    isUpdating,
    updateError,
    isDraftSaving,
    isSaving,
    draftError,
    saveError,
  }
}
